import { useState } from 'react'
import { Heart, ShoppingBag } from 'lucide-react'
import type { InventoryItemNameRow } from '@/lib/api'
import { useInventoryCart } from '@/hooks/useInventoryCart'
import { hasClearancePricing, itemAllowsCart, itemMetaLine } from '@/lib/inventoryItemDisplay'
import ItemDetailSheet from '@/components/store/ItemDetailSheet'

type Props = {
  item: InventoryItemNameRow
  isFavorite: boolean
  onToggleFavorite: () => void
}

/** Large whole dollars with smaller `$` and cents (reference-style). */
export function SuperscriptPrice({
  value,
  size = 'md',
  tone = 'default',
}: {
  value: number
  size?: 'md' | 'lg'
  tone?: 'default' | 'sale' | 'muted'
}) {
  const [whole, frac] = value.toFixed(2).split('.')
  const color =
    tone === 'sale'
      ? 'text-primary-700'
      : tone === 'muted'
        ? 'text-stone-400'
        : 'text-stone-900'
  const big = size === 'lg' ? 'text-3xl' : 'text-base sm:text-2xl'
  const small = size === 'lg' ? 'mt-1 text-sm' : 'mt-px text-[0.55rem] sm:mt-0.5 sm:text-xs'
  return (
    <span className={`inline-flex items-start leading-none tabular-nums ${color}`}>
      <span className={`font-semibold ${small}`}>$</span>
      <span className={`font-bold tracking-tight ${big}`}>{whole}</span>
      <span className={`font-semibold ${small}`}>{frac}</span>
    </span>
  )
}

export default function InventoryItemTile({ item, isFavorite, onToggleFavorite }: Props) {
  const [open, setOpen] = useState(false)
  const { getQty } = useInventoryCart()
  const qty = getQty(item.inventory_id)
  const allowsCart = itemAllowsCart(item.in_stock)
  const onSale = hasClearancePricing(item)
  const retail = item.retail_price
  const name = item.item_name || '(No name)'
  const meta = itemMetaLine(item)

  return (
    <>
      <article
        className={`group relative flex h-full flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm sm:rounded-2xl transition-shadow hover:shadow-md ${
          qty > 0 ? 'ring-2 ring-primary-500/70' : ''
        }`}
      >
        {/* Whole card opens the item; the favorite button sits above it. */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute inset-0 z-[1] rounded-xl sm:rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          aria-label={`View ${name}`}
        />

        <div className="relative flex aspect-square items-center sm:aspect-[5/4] justify-center bg-stone-100">
          <ShoppingBag
            className={`h-7 w-7 sm:h-12 sm:w-12 ${allowsCart ? 'text-stone-300' : 'text-stone-200'}`}
            strokeWidth={1.25}
            aria-hidden
          />
          <div className="absolute left-1.5 top-1.5 flex flex-col items-start gap-1 sm:left-2 sm:top-2">
            {onSale && (
              <span className="rounded px-1 py-px text-[9px] bg-primary-600 sm:rounded-md sm:px-1.5 sm:py-0.5 sm:text-[10px] font-bold text-white">
                {item.discount_percentage}% OFF
              </span>
            )}
            {!allowsCart && (
              <span className="rounded bg-stone-800/85 px-1 py-px text-[9px] sm:rounded-md sm:px-1.5 sm:py-0.5 sm:text-[10px] font-semibold text-white">
                Out of stock
              </span>
            )}
          </div>
          {qty > 0 && (
            <span
              className="absolute bottom-1.5 left-1.5 rounded-full bg-primary-600 px-1.5 py-px text-[9px] sm:bottom-2 sm:left-2 sm:px-2 sm:py-0.5 sm:text-[11px] font-semibold text-white shadow-sm"
              aria-label={`${qty} in cart`}
            >
              {qty} in cart
            </span>
          )}
          <button
            type="button"
            onClick={onToggleFavorite}
            className="absolute right-1 top-1 z-[2] flex h-7 w-7 sm:right-2 sm:top-2 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur transition-colors hover:bg-white"
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-pressed={isFavorite}
          >
            <Heart
              className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-stone-500'}`}
            />
          </button>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5 p-2 sm:gap-1 sm:p-3">
          {/* Name takes whatever width the price leaves and ends in "…". */}
          <div className="flex min-w-0 items-center gap-1 sm:gap-2">
            <h3
              className={`min-w-0 flex-1 truncate text-[11px] font-medium sm:text-sm ${
                allowsCart ? 'text-stone-800' : 'text-stone-400'
              }`}
              title={name}
            >
              {name}
            </h3>
            <div className="shrink-0">
              {onSale ? (
                <SuperscriptPrice value={item.discounted_price!} tone="sale" />
              ) : retail != null ? (
                <SuperscriptPrice value={retail} tone={allowsCart ? 'default' : 'muted'} />
              ) : (
                <span className="text-[10px] text-stone-400 sm:text-xs">Price N/A</span>
              )}
            </div>
          </div>
          {(meta || onSale) && (
            <div className="flex min-w-0 items-center gap-1 text-[10px] sm:gap-2 sm:text-xs">
              <p className="min-w-0 flex-1 truncate text-stone-500">{meta}</p>
              {onSale && (
                <span className="shrink-0 text-stone-400 line-through tabular-nums">${retail!.toFixed(2)}</span>
              )}
            </div>
          )}
        </div>
      </article>

      {open && <ItemDetailSheet item={item} onClose={() => setOpen(false)} />}
    </>
  )
}
